"use client";

/**
 * DataTable - Reusable table component for displaying tabular data
 *
 * Provides a consistent table display with optional row click handling,
 * loading states, and empty state messages.
 */

import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableHeader,
  Skeleton,
} from "@/ui";
import { colors, spacing, radius, textStyles } from "@/ui/tokens";
import { cn } from "@/lib/utils";
import tableStyles from "./DataTable.module.css";

export interface DataTableColumn<T = Record<string, unknown>> {
  key: keyof T | string;
  header: string;
  width?: string | number;
  align?: "left" | "center" | "right";
  render?: (value: unknown, row: T, index: number) => React.ReactNode;
}

export interface DataTableProps<T = Record<string, unknown>> {
  data: T[];
  columns: DataTableColumn<T>[];
  onRowClick?: (row: T, index: number) => void;
  loading?: boolean;
  emptyMessage?: string;
  className?: string;
  stickyHeader?: boolean;
  maxHeight?: number | string;
  size?: "small" | "medium";
}

/**
 * Get nested value from object using dot notation
 */
function getValue<T>(obj: T, key: string): unknown {
  const keys = key.split(".");
  let value: unknown = obj;

  for (const k of keys) {
    if (value && typeof value === "object" && k in value) {
      value = (value as Record<string, unknown>)[k];
    } else {
      return undefined;
    }
  }

  return value;
}

/**
 * Default cell renderer
 */
function defaultRender(value: unknown): React.ReactNode {
  if (value === null || value === undefined) {
    return (
      <span style={{ ...textStyles.body2, color: colors.text.muted, fontStyle: "italic" }}>
        —
      </span>
    );
  }

  if (typeof value === "boolean") {
    return value ? "Yes" : "No";
  }

  if (typeof value === "object") {
    return JSON.stringify(value);
  }

  return String(value);
}

/**
 * Loading skeleton row
 */
function SkeletonRow<T>({
  columns,
}: {
  columns: DataTableColumn<T>[];
}) {
  return (
    <TableRow>
      {columns.map((col, i) => (
        <TableCell key={i}>
          <Skeleton variant="text" width="80%" />
        </TableCell>
      ))}
    </TableRow>
  );
}

export function DataTable<T = Record<string, unknown>>({
  data,
  columns,
  onRowClick,
  loading = false,
  emptyMessage = "No data available",
  className,
  stickyHeader = false,
  maxHeight,
  size = "small",
}: DataTableProps<T>) {
  const isEmpty = !loading && data.length === 0;

  return (
    <div
      className={cn(className)}
      style={{
        maxHeight: maxHeight,
        overflow: maxHeight ? "auto" : undefined,
        border: `1px solid ${colors.border.subtle}`,
        borderRadius: radius.md,
      }}
    >
      <Table compact={size === "small"} hoverable={!!onRowClick}>
        <TableHead>
          <TableRow>
            {columns.map((col, i) => (
              <TableHeader
                key={i}
                style={{
                  width: col.width,
                  textAlign: col.align || "left",
                  position: stickyHeader ? "sticky" : undefined,
                  top: stickyHeader ? 0 : undefined,
                  zIndex: stickyHeader ? 1 : undefined,
                }}
              >
                {col.header}
              </TableHeader>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {loading ? (
            // Show skeleton rows while loading
            Array.from({ length: 3 }).map((_, i) => (
              <SkeletonRow key={i} columns={columns} />
            ))
          ) : isEmpty ? (
            // Show empty state
            <TableRow>
              <TableCell colSpan={columns.length}>
                <div
                  style={{
                    padding: `${spacing[8]}px 0`,
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                  }}
                >
                  <span style={{ ...textStyles.body2, color: colors.text.muted }}>
                    {emptyMessage}
                  </span>
                </div>
              </TableCell>
            </TableRow>
          ) : (
            // Show data rows
            data.map((row, rowIndex) => (
              <TableRow
                key={rowIndex}
                className={cn(
                  tableStyles.row,
                  onRowClick && tableStyles.clickable
                )}
                onClick={() => onRowClick?.(row, rowIndex)}
              >
                {columns.map((col, colIndex) => {
                  const value = getValue(row, col.key as string);
                  const rendered = col.render
                    ? col.render(value, row, rowIndex)
                    : defaultRender(value);

                  return (
                    <TableCell
                      key={colIndex}
                      style={{ textAlign: col.align || "left" }}
                    >
                      {rendered}
                    </TableCell>
                  );
                })}
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
    </div>
  );
}

export default DataTable;
